import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { qualitySummaryPath, useApi } from "../api/client";
import type { QualityCell, QualityRow, QualitySummary } from "../api/types";
import { BarChart } from "../components/Charts";
import { ErrorState, Loading } from "../components/States";
import {
  SubmitterFilter,
  useSubmittedBy,
  withSubmitter,
} from "../components/SubmitterFilter";
import { Breadcrumb, SegmentedControl, Toolbar, type Segment } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtMs, fmtTokens } from "../lib/format";

type Axis = "model" | "type";

const AXIS_SEGMENTS: Segment<Axis>[] = [
  { key: "model", label: "By model" },
  { key: "type", label: "By document type" },
];

/**
 * A percentage, or an em dash when the measure does not exist.
 *
 * The whole page turns on this distinction. `0` means measured and zero;
 * `null` means nobody has measured it. Rendering both as "0%" would report an
 * unverified model as having got everything wrong, and — worse, because it
 * flatters — rendering null as blank beside a 100% would read as agreement.
 */
function Pct({ value, title }: { value: number | null; title?: string }) {
  if (value === null || value === undefined) {
    return <span className="muted" title={title ?? "Not measured"}>{EM_DASH}</span>;
  }
  return <span>{value.toFixed(1)}%</span>;
}

/** Verdict mix as a compact set of pills, busiest first. */
function Verdicts({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="muted">{EM_DASH}</span>;
  return (
    <span className="pills">
      {entries.map(([v, n]) => (
        <span
          key={v}
          className={`pill ${
            v === "VALID" ? "pill-good"
            : v === "SUSPECT" || v === "UNREADABLE" ? "pill-critical"
            : v === "EXPIRED" || v === "INCOMPLETE" ? "pill-warning"
            : ""
          }`}
          title={`${n} ${v}`}
        >
          {v} {n}
        </span>
      ))}
    </span>
  );
}

export function Quality() {
  const [axis, setAxis] = useState<Axis>("model");
  // Narrows every extraction figure. Ground-truth totals and extraction tokens
  // per model stay platform-wide: neither is tied to a document.
  const [submittedBy, setSubmittedBy] = useSubmittedBy();
  const { data, error, loading, reload } = useApi<QualitySummary>(
    qualitySummaryPath(submittedBy || undefined),
  );

  const rows: QualityRow[] = useMemo(
    () => (axis === "model" ? data?.models ?? [] : data?.types ?? []),
    [data, axis]
  );

  const exportCsv = () => {
    if (!rows.length) return;
    downloadCsv(
      stampedName(axis === "model" ? "model-quality" : "type-quality"),
      toCsv<QualityRow>(rows, [
        { header: axis === "model" ? "Model" : "Document type",
          value: (r) => (axis === "model" ? r.model : r.document_type) },
        { header: axis === "model" ? "Document types" : "Models",
          value: (r) => r.partners.join(" | ") },
        { header: "Extractions", value: (r) => r.runs },
        { header: "Completed", value: (r) => r.completed },
        { header: "Failed", value: (r) => r.failed },
        { header: "Completion rate %", value: (r) => r.completion_rate },
        // Each measure keeps its own column and its own name. A single
        // "score" column is what lets a reader average accuracy together with
        // confidence, which is the mistake this page is built to prevent.
        { header: "Accuracy vs verified %", value: (r) => r.accuracy },
        { header: "Fields compared to truth", value: (r) => r.compared },
        { header: "Required-field completeness %", value: (r) => r.required_complete_rate },
        { header: "Runs scored", value: (r) => r.scored },
        { header: "Field fill rate %", value: (r) => r.field_fill_rate },
        { header: "Self-reported confidence %", value: (r) => r.mean_confidence },
        { header: "Confidence is constant", value: (r) => (r.flat_confidence ? "yes" : "no") },
        { header: "p50 latency ms", value: (r) => r.p50_ms },
        { header: "Extraction tokens", value: (r) => r.usage?.tokens ?? null },
      ])
    );
  };

  if (loading) return <Loading what="coverage and model quality" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const { totals, ground_truth: truth } = data;
  const label = (r: QualityCell) => (axis === "model" ? r.model : r.document_type);

  return (
    <>
      <Breadcrumb trail={[{ label: "Quality" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Coverage &amp; model quality</h1>
          <p className="page-sub">
            Which document types and models have been exercised, and how each combination performed
          </p>
        </div>
        <div className="range-group">
          <SubmitterFilter value={submittedBy} onChange={setSubmittedBy} idPrefix="quality" />
          <SegmentedControl segments={AXIS_SEGMENTS} value={axis} onChange={setAxis} label="Group by" />
        </div>
      </div>

      {/* The first thing on the page, because every number below is read in
          its light. Stated as what is and is not measured rather than as a
          warning to dismiss. */}
      {!truth.measurable ? (
        <div className="notice notice-warn">
          <strong>Accuracy is not measured yet.</strong>{" "}
          Accuracy means agreement with what a person read off the document, and that lives in
          verified ground truth. There are <strong>{truth.records}</strong> verified fields on
          record, so no model on this page has an accuracy figure — the Accuracy column shows
          {" "}{EM_DASH} rather than a number.
          <div style={{ marginTop: 10 }}>
            What the other columns do measure: whether a run <em>finished</em>, whether it produced
            every <em>required field</em>, and what the model <em>said about itself</em>. A field
            can be present, confidently reported, and wrong — none of these would catch it.
          </div>
          <div style={{ marginTop: 10 }}>
            To get real accuracy, open a document and record what it actually says; this page starts
            reporting the moment the first one is verified.{" "}
            <Link to="/documents">Go to documents</Link>
          </div>
        </div>
      ) : (
        <div className="notice">
          <strong>Accuracy covers {truth.documents} verified document
          {truth.documents === 1 ? "" : "s"}</strong> ({truth.records} fields). Every other column
          covers all {totals.runs} extractions. Read the two together — a high accuracy over a
          handful of fields is not yet a claim about a model.
        </div>
      )}

      <div className="tiles">
        <div className="tile">
          <div className="tile-label">Document types tested</div>
          <div className="tile-value">
            {totals.types_seen}
            <span className="muted" style={{ fontSize: "0.55em" }}> / {totals.types_registered}</span>
          </div>
          <div className="tile-note" title={totals.types_untested.join(", ")}>
            {totals.types_untested.length
              ? `${totals.types_untested.length} registered types have never run`
              : "every registered type has run"}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Models used</div>
          <div className="tile-value">{totals.models}</div>
          <div className="tile-note">across all extraction runs</div>
        </div>
        <div className="tile">
          <div className="tile-label">Extractions</div>
          <div className="tile-value">{totals.runs}</div>
          <div className="tile-note">{totals.scored} scoreable against requirements</div>
        </div>
        <div className="tile">
          <div className="tile-label">Verified documents</div>
          <div className="tile-value">{truth.documents}</div>
          {/* Called out as the gap it is, rather than sitting quietly as a
              zero among three healthy-looking numbers. */}
          <div className={`tile-note ${truth.documents ? "good" : "warn"}`}>
            {truth.documents ? "the basis for accuracy below" : "no accuracy can be computed"}
          </div>
        </div>
      </div>

      {rows.length ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">Extractions by {axis === "model" ? "model" : "document type"}</span>
            <span className="muted">volume, not quality</span>
          </div>
          <div className="card-body">
            <BarChart
              data={rows.map((r) => ({ label: label(r), value: r.runs }))}
              valueLabel="extractions"
            />
          </div>
        </div>
      ) : null}

      <Toolbar
        title={axis === "model" ? "Models" : "Document types"}
        subtitle="Each measure under its own name — they are not interchangeable"
        onExport={exportCsv}
        exportDisabled={!rows.length}
      />

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col" className="si">Sl</th>
                <th scope="col">{axis === "model" ? "Model" : "Document type"}</th>
                <th scope="col">{axis === "model" ? "Document types" : "Models"}</th>
                <th scope="col" className="num">Runs</th>
                <th scope="col" className="num" title="Runs that finished without erroring. Says nothing about whether the output is right.">
                  Completed
                </th>
                <th scope="col" className="num" title="Agreement with human-verified ground truth. The only accuracy on this page.">
                  Accuracy
                </th>
                <th scope="col" className="num" title="Runs that produced every field the agent is scored on, at the configured minimum length. Measures presence, not correctness.">
                  Required fields
                </th>
                <th scope="col" className="num" title="Share of individual required fields that carried a value.">
                  Field fill
                </th>
                <th scope="col" className="num" title="The model's own confidence. Self-reported, and not validated against anything.">
                  Self-reported
                </th>
                <th scope="col" className="num">p50</th>
                <th scope="col" className="num">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={label(r)}>
                  <td className="si">{i + 1}</td>
                  <td className="nowrap">
                    {axis === "type" ? (
                      <Link to={withSubmitter(`/agents/${encodeURIComponent(r.document_type)}`, submittedBy)}>
                        {r.document_type}
                      </Link>
                    ) : (
                      <span className="mono">{r.model}</span>
                    )}
                  </td>
                  <td>
                    <span className="pills">
                      {r.partners.map((p) => (
                        <span key={p} className="pill">{p}</span>
                      ))}
                    </span>
                  </td>
                  <td className="num">{r.runs}</td>
                  <td className="num">
                    <Pct value={r.completion_rate} />
                    {r.failed ? (
                      <div className="muted" style={{ fontSize: "0.85em" }}>{r.failed} failed</div>
                    ) : null}
                  </td>
                  <td className="num">
                    <Pct
                      value={r.accuracy}
                      title="No verified documents for this row, so there is nothing to compare against."
                    />
                    {r.compared ? (
                      <div className="muted" style={{ fontSize: "0.85em" }}>{r.compared} fields</div>
                    ) : null}
                  </td>
                  <td className="num">
                    <Pct value={r.required_complete_rate} />
                    {r.scored ? (
                      <div className="muted" style={{ fontSize: "0.85em" }}>
                        {r.complete_runs}/{r.scored} runs
                      </div>
                    ) : null}
                  </td>
                  <td className="num"><Pct value={r.field_fill_rate} /></td>
                  <td className="num">
                    <Pct value={r.mean_confidence} />
                    {/* A constant is not a measurement. Flagged inline rather
                        than footnoted: the number is right there, and a reader
                        comparing 95 against 82 deserves to know one of them
                        never moves. */}
                    {r.flat_confidence ? (
                      <div
                        className="pill pill-warning"
                        title={`This model reported the same confidence on every one of its ${r.runs} runs, so the figure is a constant rather than a per-document judgement. Treat it as missing.`}
                      >
                        constant
                      </div>
                    ) : null}
                  </td>
                  <td className="num nowrap">{r.p50_ms !== null ? fmtMs(r.p50_ms) : EM_DASH}</td>
                  <td className="num nowrap">
                    {r.usage?.tokens ? fmtTokens(r.usage.tokens) : EM_DASH}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Model by document type</span>
          <span className="muted">every combination that has actually run</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col" className="si">Sl</th>
                <th scope="col">Model</th>
                <th scope="col">Document type</th>
                <th scope="col" className="num">Runs</th>
                <th scope="col" className="num">Completed</th>
                <th scope="col" className="num">Accuracy</th>
                <th scope="col" className="num">Required fields</th>
                <th scope="col" className="num">Self-reported</th>
                <th scope="col">Document verdicts</th>
              </tr>
            </thead>
            <tbody>
              {data.matrix.map((c, i) => (
                <tr key={`${c.model}-${c.document_type}`}>
                  <td className="si">{i + 1}</td>
                  <td className="mono nowrap">{c.model}</td>
                  <td className="nowrap">{c.document_type}</td>
                  <td className="num">{c.runs}</td>
                  <td className="num"><Pct value={c.completion_rate} /></td>
                  <td className="num"><Pct value={c.accuracy} /></td>
                  <td className="num"><Pct value={c.required_complete_rate} /></td>
                  <td className="num"><Pct value={c.mean_confidence} /></td>
                  <td><Verdicts counts={c.verdicts} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Not a footnote. Without it the completeness column invites a
          comparison across a period when the bar itself moved. */}
      {data.requirement_versions.length ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">What “required fields” meant, and when</span>
            <span className="muted">each run is scored against the bar in force when it ran</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="si">Sl</th>
                  <th scope="col">Document type</th>
                  <th scope="col" className="num">Requirement version</th>
                  <th scope="col" className="num">Fields scored</th>
                  <th scope="col" className="num">Runs</th>
                  <th scope="col">First seen</th>
                  <th scope="col">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.requirement_versions.map((v, i) => (
                  <tr key={`${v.document_type}-${v.version}-${v.field_count}`}>
                    <td className="si">{i + 1}</td>
                    <td className="nowrap">{v.document_type}</td>
                    <td className="num">{v.version ?? EM_DASH}</td>
                    <td className="num">{v.field_count}</td>
                    <td className="num">{v.runs}</td>
                    <td className="nowrap">{v.first_seen ?? EM_DASH}</td>
                    <td className="nowrap">{v.last_seen ?? EM_DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-body">
            <p className="muted" style={{ margin: 0 }}>
              A completeness rate is only comparable within one requirement version. Where most runs
              were scored on fewer fields than the current set, the historical figure is a lower bar
              cleared, not the same bar cleared more often.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}
